import { cn } from "@/lib/utils";
import { useState } from "react";

export interface StarRatingProps {
  value: number;
  onChange?: (value: number) => void;
  size?: "sm" | "md" | "lg";
  count?: number;
  disabled?: boolean;
}

export const StarRating = ({
  value,
  onChange,
  size = "md",
  count = 5,
  disabled = false,
}: StarRatingProps) => {
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const sizeClasses = {
    sm: "testify-h-4 testify-w-4",
    md: "testify-h-6 testify-w-6",
    lg: "testify-h-8 testify-w-8",
  };

  const stars = Array.from({ length: count }, (_, i) => i + 1);

  const handleMouseEnter = (starValue: number) => {
    if (disabled) return;
    setHoverValue(starValue);
  };

  const handleMouseLeave = () => {
    if (disabled) return;
    setHoverValue(null);
  };

  const handleClick = (starValue: number) => {
    if (disabled) return;
    setIsAnimating(true);
    onChange?.(starValue);
    setTimeout(() => setIsAnimating(false), 300);
  };

  return (
    <div
      className="testify-inline-flex testify-gap-1"
      onMouseLeave={handleMouseLeave}
    >
      {stars.map((starValue) => {
        const isFilled = (hoverValue ?? value) >= starValue;

        return (
          <button
            key={starValue}
            type="button"
            className={cn(
              "testify-transition-all testify-duration-200 testify-ease-in-out testify-transform",
              isAnimating && isFilled && "testify-animate-bounce",
              disabled
                ? "testify-cursor-default testify-opacity-60"
                : "hover:testify-scale-110 testify-cursor-pointer",
            )}
            onMouseEnter={() => handleMouseEnter(starValue)}
            onClick={() => handleClick(starValue)}
            disabled={disabled}
          >
            <svg
              className={cn(
                sizeClasses[size],
                "testify-transition-colors testify-duration-200",
                isFilled
                  ? "testify-text-yellow-400 testify-fill-yellow-400"
                  : "testify-text-gray-300 testify-fill-gray-300 hover:testify-text-yellow-200 hover:testify-fill-yellow-200",
              )}
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
          </button>
        );
      })}
    </div>
  );
};
